import { deviceType } from "@tm/isomorphic-utils";
import { fontSize, fontWeight, theme } from "@tm/utility/global";
import styled, { css } from "styled-components";

export const ContextEditBar = styled.div`
  width: 100%;
  background: ${theme.whiteText};
  position: relative;
  z-index: 10;
  overflow: visible;
  padding: 12px 0;
`;

export const ContextEditInner = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 40px;

  @media ${deviceType.mobile} {
    flex-wrap: wrap;
    padding: 8px 16px;
    gap: 8px;
  }
`;

export const FieldsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 0;
  min-width: 0;
`;

export const FieldWrapper = styled.div`
  position: relative;

  &[data-disabled="true"] {
    opacity: 0.4;
    pointer-events: none;
  }
`;

export const FieldCard = styled.div<{
  $width?: string;
  $isActive?: boolean;
  $hasError?: boolean;
  $flex?: boolean;
}>`
  width: ${(p) => (p.$flex ? "auto" : p.$width || "200px")};
  ${(p) => p.$flex && "flex: 1 1 0; min-width: 0;"}
  background: ${theme.smokeWhite};
  border: 1px solid ${theme.lightSilver};
  border-radius: 8px;
  padding: 4px 8px 4px 12px;
  cursor: pointer;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 4px;

  ${(p) =>
    p.$isActive &&
    css`
      border-color: ${theme.blueText};
    `}

  ${(p) =>
    p.$hasError &&
    css`
      border-color: ${theme.redText2};
    `}

  @media ${deviceType.mobile} {
    width: 100%;
    flex: 1;
  }
`;

export const FieldLabel = styled.span<{ $isActive?: boolean }>`
  font-size: ${fontSize.font12};
  font-weight: ${fontWeight.bold};
  color: ${(p) => (p.$isActive ? theme.blueText : theme.greyText)};
  text-transform: uppercase;
  line-height: 1;
`;

export const FieldValue = styled.span`
  font-size: ${fontSize.font16};
  font-weight: ${fontWeight.black};
  color: ${theme.blackText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;
`;

export const FieldPlaceholder = styled.span`
  font-size: ${fontSize.font14};
  font-weight: ${fontWeight.regular};
  color: ${theme.greyText4};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;
`;

export const ErrorText = styled.span`
  position: absolute;
  bottom: -16px;
  left: 0;
  font-size: ${fontSize.font12};
  color: ${theme.redText2};
  white-space: nowrap;
`;

export const DropdownContainer = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  left: 0;
  z-index: 1000;
  border-radius: 8px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.24);
`;

export const CountryDropdownContainer = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  left: 0;
  width: 400px;
  z-index: 1000;

  > div {
    top: 0;
  }
`;

export const CalendarDropdownWrapper = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  left: 0;
  z-index: 1000;

  > div {
    position: relative;
    top: 0;
  }
`;

export const SearchButtonWrap = styled.div`
  width: 180px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media ${deviceType.mobile} {
    width: 100%;
    margin-top: 8px;
  }
`;

/* ──── Traveller number list – desktop (lg) ──── */

export const TravellerDropdownWrap = styled.div`
  width: 120px;
  max-height: 240px;
  overflow-y: auto;
  background: ${theme.whiteText};
  border-radius: 8px;
  box-shadow: 0px 2px 12px ${theme.blackText}26;
  padding: 4px 0;
`;

export const TravellerItem = styled.div<{ $isSelected: boolean }>`
  padding: 8px 16px;
  font-size: ${fontSize.font14};
  font-weight: ${(p) => (p.$isSelected ? fontWeight.bold : fontWeight.regular)};
  color: ${(p) => (p.$isSelected ? theme.blueText : theme.blackText)};
  background: ${(p) => (p.$isSelected ? theme.lightBlueBg : "transparent")};
  cursor: pointer;
  text-align: center;

  &:hover {
    background: ${theme.lightBlueBg};
  }
`;

/* ──── Traveller stepper – mobile (sm) ──── */

export const TravellerStepperCard = styled.div`
  background: ${theme.smokeWhite};
  border: 1px solid ${theme.lightSilver};
  border-radius: 8px;
  padding: 4px 8px 4px 12px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TravellerStepperInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

export const TravellerCounterBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid ${theme.lightSilver};
  border-radius: 8px;
  padding: 6px 10px;
  background: ${theme.whiteText};
  flex-shrink: 0;
`;

export const TravellerCounterBtn = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.disabled ? 0.3 : 1)};
  padding: 0;
  -webkit-tap-highlight-color: transparent;
`;

export const TravellerCounterValue = styled.span`
  font-size: ${fontSize.font16};
  font-weight: ${fontWeight.black};
  color: ${theme.blackText};
  min-width: 20px;
  text-align: center;
`;
